import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  FileText, 
  Shield, 
  Share2, 
  Activity, 
  Settings, 
  LogOut,
  Plus,
  Search,
  Menu,
  X,
  Scroll,
  AlertTriangle
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useResponsive } from '../../hooks/useResponsive';
import { useNavigate } from 'react-router-dom';
import Button from '../atoms/Button';
import Card from '../atoms/Card';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  onUploadClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  activeSection, 
  onSectionChange, 
  onUploadClick 
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { isMobile } = useResponsive();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if user is admin (richierodney434@gmail.com)
  const isAdmin = user?.email === 'richierodney434@gmail.com';

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'documents', label: 'My Documents', icon: FileText },
    { id: 'vault', label: 'Private Vault', icon: Shield },
    { id: 'shared', label: 'Shared with Me', icon: Share2 },
    { id: 'will-witness', label: 'Will & Witness', icon: Scroll },
    { id: 'activity', label: 'Activity Log', icon: Activity },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Add audit dashboard for admin
  if (isAdmin) {
    navigationItems.push({ id: 'audit', label: 'Audit Dashboard', icon: AlertTriangle });
  }

  const handleSectionChange = (section: string) => {
    if (section === 'audit') {
      navigate('/audit-dashboard');
    } else {
      onSectionChange(section);
    }
    
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 lg:p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
            <Shield className="h-4 w-4 lg:h-6 lg:w-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg lg:text-xl font-bold text-gray-900 truncate">Aegis Vault</h1>
            <p className="text-xs text-gray-500 hidden sm:block">Secure Document Management</p>
          </div>
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              icon={X}
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-4 border-b border-gray-200">
        <div className="space-y-2">
          <Button
            variant="primary"
            size="sm"
            icon={Plus}
            className="w-full justify-start"
            onClick={() => {
              onUploadClick?.();
              if (isMobile) setIsMobileMenuOpen(false);
            }}
          >
            Secure Upload
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={Search}
            className="w-full justify-start"
            onClick={() => handleSectionChange('documents')}
          >
            Quick Search
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const isActive = activeSection === item.id || 
                            (item.id === 'audit' && window.location.pathname === '/audit-dashboard');
            const Icon = item.icon;

            return (
              <motion.button
                key={item.id}
                onClick={() => handleSectionChange(item.id)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-left
                  ${isActive 
                    ? 'bg-primary-50 text-primary-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon size={18} className="flex-shrink-0" />
                <span className="truncate">{item.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="ml-auto w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-gray-200">
        <Card padding="sm" className="mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium text-primary-700">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || user?.walletAddress || 'No email'}
              </p>
            </div>
          </div>
        </Card>
        
        <Button
          variant="ghost"
          size="sm"
          icon={LogOut}
          onClick={() => {
            logout();
            if (isMobile) setIsMobileMenuOpen(false);
          }}
          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          Sign Out
        </Button>
      </div>
    </div>
  );

  // Mobile version with overlay
  if (isMobile) {
    return (
      <>
        {/* Mobile Menu Button */}
        <div className="fixed top-4 left-4 z-40">
          <Button
            variant="primary"
            size="sm"
            icon={Menu}
            onClick={() => setIsMobileMenuOpen(true)}
            className="bg-white shadow-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
          />
        </div>

        {/* Mobile Sidebar Overlay */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 z-50"
                onClick={() => setIsMobileMenuOpen(false)}
              />
              
              {/* Sidebar */}
              <motion.div
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed left-0 top-0 bottom-0 w-72 bg-white shadow-xl z-50"
              >
                <SidebarContent />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </>
    );
  }

  // Desktop version
  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
      <SidebarContent />
    </div>
  );
};

export default Sidebar;